<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProjectsPanel } from "../../composables/useProjectsPanel.js";
import { useProjectsList } from "../../composables/useProjectsList.js";
import { useInfiniteScroll } from "../../composables/useInfiniteScroll.js";
import { truncateMiddle } from "../../composables/useTextTruncate.js";
import Spinner from "../ui/Spinner.vue";
import PanelSortMenu from "../ui/PanelSortMenu.vue";
import DownloadOnlyModal from "../ui/DownloadOnlyModal.vue";
import ProjectCardMenu from "../ui/ProjectCardMenu.vue";
import DocumentCardMenu from "../ui/DocumentCardMenu.vue";
import { useDocumentSync } from "../../composables/useDocumentSync.js";

const props = defineProps({
  isActive: { type: Boolean, default: false }, // panel is mounted app-wide (v-show); only fetch once it's actually opened
});
const emit = defineEmits(["close"]);

const route = useRoute();
const router = useRouter();

// Non-annotatable docs (e.g. standalone/singleton uploads that can't be
// previewed) open a permission-gated download popup instead of navigating —
// same behavior as Home.vue's document table.
const downloadOnlyDoc = ref(null);

function openDoc(doc) {
  if (doc.isAnnotatable === false) {
    downloadOnlyDoc.value = doc;
    return;
  }
  router.push({ name: "v2-document", params: { projectId: doc.projectId ?? currentProjectId.value, docId: doc.id } });
}
function isActiveDoc(doc) {
  return route.name === "v2-document" && route.params.docId === doc.id;
}

const {
  currentProjectId, currentProject,
  projects, isLoadingProjects, isLoadingMoreProjects, hasMoreProjects, loadMoreProjects, ensureProjectsLoaded, resortProjects, openProject, viewProject,
  starredDocs, otherDocs, downloadOnlyDocs, isLoadingDocs, isLoadingMoreDocs, hasMoreDocs, loadMoreDocs, resortDocs,
  activate,
} = useProjectsPanel();
const { removeProject } = useProjectsList();
const documentSync = useDocumentSync();

// Starred / All Documents / Download only files — one row template for all
// three. All Documents always shows (it carries the empty state's context);
// the other two only when they have something in them.
const docSections = computed(() => [
  { key: "starred", label: "Starred", docs: starredDocs.value, labelTestId: "projects-panel-starred-label", rowTestId: "projects-panel-starred-doc" },
  { key: "all", label: "All Documents", docs: otherDocs.value, labelTestId: "projects-panel-docs-label", rowTestId: "projects-panel-doc", alwaysShow: true },
  { key: "download-only", label: "Download only files", docs: downloadOnlyDocs.value, labelTestId: "projects-panel-download-only-label", rowTestId: "projects-panel-download-only-doc" },
].filter((section) => section.alwaysShow || section.docs.length));

const SORT_OPTIONS = [
  { key: "NAME", label: "Name" },
  { key: "DATE_ADDED", label: "Date added" },
  { key: "LAST_MODIFIED", label: "Last modified" },
];
const projectsSortBy = ref("LAST_MODIFIED");
const docsSortBy = ref("DATE_ADDED");

function onSelectProjectsSort(key) {
  projectsSortBy.value = key;
  resortProjects(key, "DESC");
}
function onSelectDocsSort(key) {
  docsSortBy.value = key;
  resortDocs(key, "DESC");
}

watch(() => props.isActive, (active) => { if (active) activate(); }, { immediate: true });

// One sentinel serves whichever list is currently showing (projects or docs).
// Must return the underlying promise — useInfiniteScroll awaits it to know
// when the fetch actually lands before re-measuring the sentinel's position.
const sentinelEl = ref(null);
useInfiniteScroll(sentinelEl, () => {
  if (currentProjectId.value) return loadMoreDocs();
  return loadMoreProjects();
});

// The switcher dropdown renders its own separate (scrollable) list, so it
// needs its own sentinel — the one above lives in the docs/projects browse
// section, which isn't even mounted while the switcher is showing (we're
// inside a project, so that section is displaying the current project's docs).
const switcherSentinelEl = ref(null);
useInfiniteScroll(switcherSentinelEl, () => loadMoreProjects());

// Project switcher dropdown, shown from the header regardless of mode.
// Teleported to <body> — the panel box (rounded-corner overflow-hidden, see
// template) and AppLayout's animated-width panel wrapper both clip anything
// absolutely positioned inside them, which was cutting the dropdown down to a
// sliver instead of letting it show/scroll through the full project list.
const showSwitcher = ref(false);
const switcherAnchorEl = ref(null);
const switcherDropdownEl = ref(null);
const switcherPos = ref({ top: 0, left: 0, maxHeight: 288 });

async function toggleSwitcher() {
  showSwitcher.value = !showSwitcher.value;
  if (showSwitcher.value) {
    ensureProjectsLoaded();
    await nextTick();
    const rect = switcherAnchorEl.value?.getBoundingClientRect();
    if (rect) {
      const top = rect.bottom + 8;
      // Fill the actual remaining space below the anchor (minus a small
      // margin) rather than a fixed height — a flat max-h-72 looked cramped
      // and, worse, often ended exactly on an item boundary with nothing cut
      // off, giving no visual hint that the list scrolls at all.
      const maxHeight = Math.max(160, window.innerHeight - top - 16);
      switcherPos.value = { top, left: rect.left, maxHeight };
    }
  }
}
// From the document annotation view, switching projects shouldn't navigate
// away from the document — just show that project's docs in the panel;
// opening one of its files is what actually navigates (see openDoc). On the
// Project page itself, switching still navigates to the picked project, same
// as before.
function switchToProject(id) {
  showSwitcher.value = false;
  if (route.name === "v2-document") {
    viewProject(id);
  } else {
    openProject(id);
  }
}

// Teleporting moves the dropdown out of the panel's DOM subtree, so clicks
// inside it no longer bubble into the panel's own close-on-click handler —
// needs its own outside-click check instead (same pattern as ProfileMenu.vue).
function onDocClick(e) {
  if (!showSwitcher.value) return;
  const insideAnchor = switcherAnchorEl.value?.contains(e.target);
  const insideDropdown = switcherDropdownEl.value?.contains(e.target);
  if (!insideAnchor && !insideDropdown) showSwitcher.value = false;
}
onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));
</script>

<template>
  <div data-testid="projects-panel" class="bg-white dark:bg-inherit w-[256px] h-full">
    <div data-testid="projects-panel-body" class="flex mt-3 flex-col h-full w-[244px] bg-gray-100 dark:bg-slate-900 rounded-t-2xl overflow-hidden"
      @click="showSwitcher = false">

      <!-- Header — same gray as the list body below it, one continuous surface -->
      <div data-testid="projects-panel-header" class="flex border-b-2 border-white dark:border-slate-600 items-center justify-between px-4 py-3 shrink-0">
        <div class="relative flex-1 min-w-0" @click.stop>
          <!-- Standalone doc (singleton project) — no other project to switch to -->
          <span v-if="currentProjectId && currentProject?.isSingleton"
            data-testid="projects-panel-singleton-title"
            class="block truncate text-[15px] font-semibold text-slate-800 dark:text-slate-100">
            <div class="flex items-baseline gap-1.5 flex-1 min-w-0">
              <span class="text-[15px] font-semibold text-slate-800 dark:text-slate-100 truncate">Docs</span>
              <span class="text-[12px] text-slate-400 dark:text-slate-500 truncate">(No project assigned)</span>
            </div>
          </span>

          <button v-else ref="switcherAnchorEl"
            data-testid="projects-panel-switcher"
            class="flex items-center gap-1.5 text-[15px] font-semibold text-slate-800 dark:text-slate-100 bg-transparent border-0 cursor-pointer p-0 hover:text-brand transition-colors max-w-full"
            @click="toggleSwitcher">
            <span data-testid="projects-panel-title" class="truncate">{{ currentProjectId ? (currentProject?.name ?? "Project") : "All Projects" }}</span>
            <svg class="shrink-0 text-slate-500" width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>

          <!-- Project switcher dropdown — teleported to escape this panel's
               (and AppLayout's) clipping overflow-hidden ancestors -->
          <Teleport to="body">
            <div v-if="showSwitcher && !currentProject?.isSingleton" ref="switcherDropdownEl"
              data-testid="projects-panel-switcher-menu"
              :style="{ top: switcherPos.top + 'px', left: switcherPos.left + 'px', maxHeight: switcherPos.maxHeight + 'px' }"
              class="fixed w-60 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-[300]">
              <p
                class="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 px-3 pt-1 pb-1.5 m-0">
                Projects</p>
              <div v-if="isLoadingProjects" class="flex justify-center py-4">
                <Spinner size="18px" />
              </div>
              <template v-else>
                <button v-for="p in projects" :key="p.id" :title="p.name" data-testid="projects-panel-switcher-option" :class="p.id === currentProjectId
                  ? 'text-brand bg-orange-50 dark:bg-orange-950/30'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'"
                  class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors"
                  @click="switchToProject(p.id)">
                  <svg class="shrink-0 text-slate-400" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
                    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" stroke-width="1.8" />
                  </svg>
                  <span class="truncate flex-1">{{ truncateMiddle(p.name) }}</span>
                </button>
                <p v-if="!projects.length" data-testid="projects-panel-switcher-empty" class="text-xs text-slate-400 text-center py-3 m-0">No projects yet</p>

                <!-- Infinite scroll sentinel -->
                <div ref="switcherSentinelEl" class="h-px" />
                <div v-if="isLoadingMoreProjects" class="flex justify-center py-2">
                  <Spinner size="14px" />
                </div>
              </template>
            </div>
          </Teleport>
        </div>

        <!-- Close -->
        <button
          data-testid="projects-panel-close"
          class="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-colors shrink-0 ml-2"
          @click="emit('close')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <!-- ══ Inside a project: its documents ══════════════════════════════════ -->
      <template v-if="currentProjectId">
        <div v-if="isLoadingDocs" data-testid="projects-panel-loading" class="flex justify-center py-10">
          <Spinner size="22px" />
        </div>

        <div v-else class="flex-1 min-h-0 overflow-y-auto py-3 px-2">
          <template v-for="(section, index) in docSections" :key="section.key">
            <div v-if="index > 0" class="mx-2 my-2 h-px bg-slate-200 dark:bg-slate-700" />
            <p :data-testid="section.labelTestId" class="text-[11px] text-slate-400 dark:text-slate-500 px-2 pt-1 pb-1 m-0">{{ section.label }} ({{ section.docs.length }})</p>
            <div v-for="doc in section.docs" :key="doc.id" :title="doc.name" :data-testid="section.rowTestId" :class="isActiveDoc(doc)
              ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
              : 'hover:bg-slate-200 dark:hover:bg-slate-700/60'"
              class="flex items-center gap-2.5 px-2 py-2.5 rounded-xl cursor-pointer group transition-colors"
              @click="openDoc(doc)">
              <svg class="shrink-0"
                :class="isActiveDoc(doc) ? 'text-blue-500 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'"
                width="20" height="22" viewBox="0 0 24 26" fill="none">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor"
                  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
              <span
                :class="isActiveDoc(doc) ? 'text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-100'"
                :title="doc.name ?? 'Untitled'" class="flex-1 text-[14px] font-medium truncate min-w-0">{{ truncateMiddle(doc.name ?? "Untitled") }}</span>
              <DocumentCardMenu v-if="currentProject?.id"
                data-testid="projects-panel-doc-menu"
                :document="doc"
                :project="currentProject"
                allow-favorite-toggle
                trigger-class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600 border-0 bg-transparent cursor-pointer shrink-0 transition-colors"
                @renamed="documentSync.renamed(doc.id, $event.name)"
                @starred="documentSync.favoriteChanged(doc.id, true)"
                @unstarred="documentSync.favoriteChanged(doc.id, false)"
                @deleted="documentSync.removed(doc.id, currentProject)"
                @rejected="documentSync.removed(doc.id, currentProject)"
              />
            </div>
          </template>

          <p v-if="!starredDocs.length && !otherDocs.length && !downloadOnlyDocs.length"
            data-testid="projects-panel-docs-empty"
            class="text-xs text-slate-400 dark:text-slate-500 text-center py-6 px-4 m-0">No documents in this project
          </p>

          <!-- Infinite scroll sentinel -->
          <div ref="sentinelEl" class="h-px" />
          <div v-if="isLoadingMoreDocs" class="flex justify-center py-3">
            <Spinner size="16px" />
          </div>
        </div>
      </template>

      <!-- ══ Not inside a project: browse all projects ═════════════════════════ -->
      <template v-else>
        <div v-if="isLoadingProjects" data-testid="projects-panel-loading" class="flex justify-center py-10">
          <Spinner size="22px" />
        </div>

        <div v-else class="flex-1 min-h-0 overflow-y-auto py-3 px-2">
          <div class="flex items-center px-2 pt-1 pb-1 gap-1">
            <p data-testid="projects-panel-projects-label" class="text-[11px] text-slate-400 dark:text-slate-500 flex-1 m-0">All Projects</p>
            <PanelSortMenu data-testid="projects-panel-sort" :sort-by="projectsSortBy" :options="SORT_OPTIONS" @select="onSelectProjectsSort" />
          </div>

          <div v-for="p in projects" :key="p.id" :title="p.name"
            data-testid="projects-panel-project"
            class="flex items-center gap-2.5 px-2 py-2.5 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700/60 cursor-pointer transition-colors"
            @click="openProject(p.id)">
            <svg class="shrink-0 text-slate-400" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" stroke-width="1.8" />
            </svg>
            <span data-testid="projects-panel-project-name" class="flex-1 text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate min-w-0">{{ p.name
            }}</span>
            <ProjectCardMenu :project="p" @deleted="removeProject(p.id)" @rejected="removeProject(p.id)" />
          </div>

          <p v-if="!projects.length" data-testid="projects-panel-projects-empty" class="text-xs text-slate-400 dark:text-slate-500 text-center py-6 px-4 m-0">No
            projects yet</p>

          <!-- Infinite scroll sentinel -->
          <div ref="sentinelEl" class="h-px" />
          <div v-if="isLoadingMoreProjects" class="flex justify-center py-3">
            <Spinner size="16px" />
          </div>
        </div>
      </template>
    </div>

    <DownloadOnlyModal
      v-if="downloadOnlyDoc"
      :document="downloadOnlyDoc"
      :project="currentProject"
      @close="downloadOnlyDoc = null"
    />
  </div>
</template>
